<template>
  <f7-page>
    <f7-navbar :title="budget ? $t('title.reportFor', [budget.name]) : $t('title.report')"
               :back-link="$t('action.back')"
    />
    <form>
      <!-- Type of report -->
      <f7-block>
        <f7-block-title>{{$t('title.reportType')}}</f7-block-title>
        <f7-segmented strong tag="p">
          <f7-button :active="reportType === 'expense'"
                     :text="$t('option.expenseReport')"
                     @click="reportType = 'expense'"
                     v-if="isMaster"
          />
          <f7-button :active="reportType === 'budget'"
                     :text="$t('option.budgetReport')"
                     @click="reportType = 'budget'"
          />
          <f7-button :active="reportType === 'xero'"
                     :text="$t('option.xeroReport')"
                     @click="reportType = 'xero'"
                     v-if="isMaster"
          />
        </f7-segmented>
      </f7-block>
      <!-- Report filtering -->
      <f7-block>
        <report-filter v-show="!isXero"
                       @input="filter = $event"
                       :report-type="reportType"
                       class="filters"
        />
      </f7-block>
      <f7-block v-if="['expense', 'budget'].includes(reportType)">
        <f7-segmented raised tag="p">
          <f7-button large fill @click="downloadReport" text="Download Report"/>
          <f7-button large fill @click="sendReport" :text="$t('action.sendEmailReport')"/>
        </f7-segmented>
        <p v-if="reportType === 'expense'">{{$t('text.expenseReport')}}</p>
        <p v-if="reportType === 'budget'">{{$t('text.budgetReport', {name: budget.name})}}</p>
      </f7-block>

      <f7-block v-else-if="reportType === 'xero'">
        <p v-if="!xeroConnected">
          {{$t('text.xeroNotConnected[0]')}}
          <f7-link :href="links.company">{{$t('text.xeroNotConnected[1]')}}</f7-link>
          {{$t('text.xeroNotConnected[2]')}}
        </p>
        <p v-else>
          <b>{{$t('label.xeroOrganisation')}}: </b>{{company.xeroOrg}}
        </p>
        <f7-card :title="$t('title.unclassifiedExpenses')" class="text-color-white bg-color-pink"
                 v-if="company.unclassifiedExpenses > 0"
        >
          <f7-card-content>
            {{$t('text.unclassifiedExpenses', {count: company.unclassifiedExpenses})}}
          </f7-card-content>
        </f7-card>
        <f7-button large fill @click="sendReport" :disabled="isXero && !xeroConnected">
          <img src="@/assets/img/xero-logo-inv-200px.png" alt="Xero logo" class="xero-logo"/>
          {{$t('action.sendXeroReport')}}
        </f7-button>
        <p>{{$t('text.xeroReport')}}</p>
      </f7-block>
    </form>
  </f7-page>
</template>

<script>
  import api from 'aio-api'
  import helpers from '@/utils/helpers'
  import ReportFilter from '@/components/ReportFilter'
  import {saveAs} from 'file-saver'
  import moment from 'moment'

  export default {
    name: 'report-page',
    components: { ReportFilter },
    computed: {
      isMaster () {
        return !this.budget.parentId
      },
      isXero () {
        return this.reportType === 'xero'
      },
    },
    async created () {
      let budgetId = this.$f7route.params.budget_id
      if (!budgetId) {
        await this.$store.dispatch('budgets/fetch')
        budgetId = this.budgets.find(b => !b.parentId).potId
      }
      if (budgetId) {
        this.budget = await api.getBudget(budgetId)
        this.reportType = this.isMaster ? 'expense' : 'budget'
        if (this.isMaster) {
          try {
            await api.xero.check()
            this.xeroConnected = true
          } catch (error) {
            this.xeroConnected = false
          }
        }
      }
      this.company = await api.getMyCompany()
    },
    data () {
      return {
        budget: {},
        filter: {},
        reportType: undefined,
        xeroConnected: false,
        company: {
          unclassifiedExpenses: 0,
        },
      }
    },
    methods: {
      async sendReport () {
        const validDateRange = this.filter.fromMonth && this.filter.fromYear && this.filter.toMonth && this.filter.toYear
        if (!(this.isXero || validDateRange)) {
          this.$f7.dialog.alert(this.$t('validation.dateRange'), '')
        } else {
          try {
            switch (this.reportType) {
              case 'expense':
                await api.getExpenseReport(this.filter.fromMonth, this.filter.fromYear, this.filter.toMonth, this.filter.toYear)
                this.$f7.dialog.alert(this.$t('dialog.createReportSuccess'), '', () => {
                  this.goBack()
                })
                break
              case 'budget':
                await api.getBudgetReport(this.budget.potId, this.filter.fromMonth, this.filter.fromYear, this.filter.toMonth, this.filter.toYear)
                this.$f7.dialog.alert(this.$t('dialog.createReportSuccess'), '', () => {
                  this.goBack()
                })
                break
              case 'xero':
                api.xero.sync()
                this.$f7.dialog.alert(this.$t('dialog.createXeroReportSuccess'), '', () => {
                  this.goBack()
                })
                break
            }
          } catch (error) {
            //TODO: i18n error message
            this.$f7.dialog.alert(error, '')
          }
        }
      },
      async downloadReport () {
        if (this.reportType === 'expense') {
          const report = await api.report.expense.get(this.filter)
          const reportBlob = new Blob([report], { type: 'text/plain;charset=UTF-8' })
          saveAs(reportBlob, `aio-report_${moment().format('YYYY-MM-DD')}.csv`)
          return
        }
        if (this.reportType === 'budget') {
          const report = await api.report.budget.get(this.budget.potId, this.filter)
          const reportBlob = new Blob([report], { type: 'text/plain;charset=UTF-8' })
          saveAs(reportBlob, `aio-report_${this.budget.name}_${moment().format('YYYY-MM-DD')}.csv`)
          return
        }
      },
    },
    mixins: [helpers],
  }
</script>

<style lang="scss" scoped>
  .filters {
    z-index: 99;
  }

  .xero-logo {
    float: left;

    .md & {
      height: 48px;
    }

    .ios & {
      height: 43px;
    }
  }
</style>
